import { useQuery } from '@tanstack/react-query';
import { AdminShell } from '../components/layout/AdminShell';
import { FacilityManager } from '../components/facilities/FacilityManager';
import { PERMISSIONS } from '../lib/constants';
import { fetchFacilities, fetchMe } from '../lib/identityRegistry';

export function InstanceManagementPage(): JSX.Element {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const facilitiesQuery = useQuery({
    queryKey: ['facilities'],
    queryFn: fetchFacilities,
    enabled: Boolean(meQuery.data?.permissions.includes(PERMISSIONS.MANAGE_FACILITIES)),
  });

  if (meQuery.isLoading) {
    return (
      <div className="route-skeleton route-skeleton--fullscreen">
        <div className="route-skeleton__pulse" />
        <p>Loading instances...</p>
      </div>
    );
  }

  if (meQuery.error || !meQuery.data) {
    return (
      <div className="center-screen">
        <div className="panel unauthorized">
          <h1>Unable to load profile</h1>
          <p className="panel__copy">
            {meQuery.error instanceof Error ? meQuery.error.message : 'Request failed'}
          </p>
        </div>
      </div>
    );
  }

  if (!meQuery.data.permissions.includes(PERMISSIONS.MANAGE_FACILITIES)) {
    return (
      <AdminShell title="Sense AI — Instance management" subtitle={meQuery.data.email}>
        <div className="center-screen">
          <div className="panel unauthorized">
            <h1>Not authorized</h1>
            <p className="panel__copy">
              You need the <code>{PERMISSIONS.MANAGE_FACILITIES}</code> permission to manage
              instances.
            </p>
          </div>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell title="Sense AI — Instance management" subtitle={meQuery.data.email}>
      <section className="panel admin-panel">
        {facilitiesQuery.isLoading ? (
          <p className="hint-text">Loading facilities...</p>
        ) : facilitiesQuery.error ? (
          <div className="form-error">
            {facilitiesQuery.error instanceof Error
              ? facilitiesQuery.error.message
              : 'Failed to load facilities'}
          </div>
        ) : (
          <FacilityManager facilities={facilitiesQuery.data ?? []} />
        )}
      </section>
    </AdminShell>
  );
}
